/* eslint-disable react-hooks/set-state-in-effect, react-hooks/preserve-manual-memoization */
import {
  MutableRefObject,
  RefCallback,
  useCallback,
  useEffect,
  useState
} from "react"

import type { DismissReason, ToastInternal } from "./types"

export const useMountedLifecycle = (
  exitTimersRef: MutableRefObject<Map<string, number>>,
  autoDismissTimersRef: MutableRefObject<Map<string, number>>,
  focusQueueRef: MutableRefObject<Set<string>>
) => {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    const exitTimers = exitTimersRef.current
    const autoTimers = autoDismissTimersRef.current
    const focusQueue = focusQueueRef.current

    return () => {
      exitTimers.forEach((timer) => {
        window.clearTimeout(timer)
      })
      autoTimers.forEach((timer) => {
        window.clearTimeout(timer)
      })
      exitTimers.clear()
      autoTimers.clear()
      focusQueue.clear()
    }
  }, [autoDismissTimersRef, exitTimersRef, focusQueueRef])

  return mounted
}

type AutoDismissOptions = {
  visibleToasts: ToastInternal[]
  defaultAutoDismissDelay: number
  dismissToast: (id: string, reason?: DismissReason) => void
  autoDismissTimersRef: MutableRefObject<Map<string, number>>
}

export const useAutoDismissTimers = ({
  visibleToasts,
  defaultAutoDismissDelay,
  dismissToast,
  autoDismissTimersRef
}: AutoDismissOptions) => {
  useEffect(() => {
    const visibleActive = visibleToasts.filter(
      (toast) => toast.status === "visible"
    )

    visibleActive.forEach((toast) => {
      const allowAutoDismiss = toast.options.autoDismiss
      const existingTimer = autoDismissTimersRef.current.get(toast.id)

      if (allowAutoDismiss) {
        if (!existingTimer) {
          const delay =
            toast.options.autoDismissDelay ?? defaultAutoDismissDelay
          const timer = window.setTimeout(() => {
            dismissToast(toast.id, "timeout")
          }, delay)
          autoDismissTimersRef.current.set(toast.id, timer)
        }
      } else if (existingTimer) {
        window.clearTimeout(existingTimer)
        autoDismissTimersRef.current.delete(toast.id)
      }
    })

    autoDismissTimersRef.current.forEach((timer, id) => {
      const stillVisible = visibleActive.some((toast) => toast.id === id)
      if (!stillVisible) {
        window.clearTimeout(timer)
        autoDismissTimersRef.current.delete(id)
      }
    })
  }, [
    autoDismissTimersRef,
    defaultAutoDismissDelay,
    dismissToast,
    visibleToasts
  ])
}

type FocusManagerOptions = {
  visibleToasts: ToastInternal[]
  focusQueueRef: MutableRefObject<Set<string>>
  toastRefs: MutableRefObject<Map<string, HTMLDivElement | null>>
}

export const useFocusManager = ({
  visibleToasts,
  focusQueueRef,
  toastRefs
}: FocusManagerOptions) => {
  useEffect(() => {
    visibleToasts.forEach((toast) => {
      if (toast.status !== "visible") {
        focusQueueRef.current.delete(toast.id)
        return
      }

      if (!focusQueueRef.current.has(toast.id)) {
        return
      }

      const element = toastRefs.current.get(toast.id)
      if (element) {
        element.focus()
        focusQueueRef.current.delete(toast.id)
      }
    })
  }, [focusQueueRef, toastRefs, visibleToasts])
}

export const useRegisterToastRef = (
  toastRefs: MutableRefObject<Map<string, HTMLDivElement | null>>
) =>
  useCallback(
    (id: string): RefCallback<HTMLDivElement> =>
      (node) => {
        if (node) {
          toastRefs.current.set(id, node)
          return
        }
        toastRefs.current.delete(id)
      },
    [toastRefs]
  )

export const useDismissHandlerFactory = (
  dismissToast: (id: string, reason?: DismissReason) => void
) =>
  useCallback((id: string) => () => dismissToast(id, "manual"), [dismissToast])
